import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import "./css/Profile.css";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;

export default function Profile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");

  // Form fields
  const [description, setDescription] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Translations
  const t = {
    en: {
      generalSettings: "General Settings",
      back: "Back",
      profileCompletionWarning: "Please upload the company logo and add the company description to complete the profile!",
      companyLogo: "Company Logo",
      companyDescription: "Company Description",
      descriptionPlaceholder: "Write your company description here...",
      save: "Save",
      readOnlyWarning: "The data given below is read only, if you want to update any details given below",
      contactUs: "Contact Us",
      phoneNumber: "Phone Number",
      companyName: "Company Name",
      whatsappNumber: "Whatsapp Number",
      companyEmail: "Company Email",
      authorizedPersonName: "Authorized Person Name",
      authorizedPersonDesignation: "Authorized Person Designation",
      addressLine1: "Address Line 1",
      addressLine2: "Address Line 2",
      city: "City",
      emirate: "Emirate",
      poBox: "PO Box",
      locationOnMap: "Location On Map",
      clickToSelect: "Click Here to Select",
      loading: "Loading",
      saving: "Saving...",
    },
    ar: {
      generalSettings: "الإعدادات العامة",
      back: "رجوع",
      profileCompletionWarning: "يرجى تحميل شعار الشركة وإضافة وصف الشركة لإكمال الملف الشخصي!",
      companyLogo: "شعار الشركة",
      companyDescription: "وصف الشركة",
      descriptionPlaceholder: "اكتب وصف الشركة هنا...",
      save: "حفظ",
      readOnlyWarning: "البيانات المذكورة أدناه للقراءة فقط، إذا كنت تريد تحديث أي تفاصيل أدناه",
      contactUs: "اتصل بنا",
      phoneNumber: "رقم الهاتف",
      companyName: "اسم الشركة",
      whatsappNumber: "رقم الواتساب",
      companyEmail: "البريد الإلكتروني للشركة",
      authorizedPersonName: "اسم الشخص المخول",
      authorizedPersonDesignation: "منصب الشخص المخول",
      addressLine1: "عنوان السطر 1",
      addressLine2: "عنوان السطر 2",
      city: "المدينة",
      emirate: "الإمارة",
      poBox: "صندوق البريد",
      locationOnMap: "الموقع على الخريطة",
      clickToSelect: "انقر هنا للاختيار",
      loading: "جاري التحميل",
      saving: "جاري الحفظ...",
    },
  }[lang];

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profileData = data.response;
          
          // Set language preference
          if (profileData.language_preference) {
            localStorage.setItem("app_language", profileData.language_preference);
            setLang(profileData.language_preference);
          }

          setProfile(profileData);
          setDescription(profileData.description || "");
          
          // Set logo preview if profile_image exists
          if (profileData.profile_image && profileData.profile_image.url) {
            setLogoPreview(profileData.profile_image.url);
          }
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        showToast(err.message || "Failed to load profile", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize map for location display
  const initializeMap = useCallback(() => {
    if (!profile?.location_coordinates || profile.location_coordinates.length !== 2) {
      return;
    }

    if (!window.google || !window.google.maps || !mapRef.current) {
      return;
    }

    // Prevent multiple initializations
    if (mapInstanceRef.current) {
      return;
    }

    // location_coordinates is [longitude, latitude] format
    const lng = parseFloat(profile.location_coordinates[0]);
    const lat = parseFloat(profile.location_coordinates[1]);

    if (isNaN(lat) || isNaN(lng)) {
      return;
    }

    const center = { lat, lng };

    const map = new window.google.maps.Map(mapRef.current, {
      center: center,
      zoom: 15,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      disableDefaultUI: false,
    });

    mapInstanceRef.current = map;

    // Create marker (non-draggable, view-only)
    const marker = new window.google.maps.Marker({
      position: center,
      map: map,
      draggable: false,
    });

    markerRef.current = marker;
  }, [profile]);

  // Load Google Maps script and initialize map
  useEffect(() => {
    if (!profile?.location_coordinates || profile.location_coordinates.length !== 2) {
      return;
    }

    if (window.google && window.google.maps) {
      setTimeout(() => initializeMap(), 200);
      return;
    }

    // Load Google Maps script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.google && window.google.maps) {
        setTimeout(() => initializeMap(), 200);
      }
    };

    script.onerror = () => {
      console.error("Failed to load Google Maps script");
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
    };
  }, [profile, initializeMap]);

  // Handle logo file selection
  const handleLogoChange = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    
    const file = e.target.files[0];
    
    if (!file) {
      return;
    }

    // Validate file type
    const validTypes = [
      "image/png",
      "image/jpg",
      "image/jpeg",
      "image/svg+xml",
      "image/webp",
      "image/avif",
      "image/heic",
      "image/heif",
    ];

    if (!validTypes.includes(file.type)) {
      showToast(
        lang === "ar"
          ? "يجب أن يكون تنسيق الملف PNG، JPG، JPEG، SVG، WebP، AVIF، HEIC"
          : "File format should be PNG, JPG, JPEG, SVG, WebP, AVIF, HEIC",
        "error"
      );
      // Reset input value after validation error
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Validate file size (2MB = 2 * 1024 * 1024 bytes)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast(
        lang === "ar"
          ? "يجب أن يكون حجم الملف أقل من 2 ميجابايت"
          : "File size should be less than 2MB",
        "error"
      );
      // Reset input value after validation error
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      // Reset input value after successful file selection to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };
    reader.onerror = () => {
      // Reset input value on error
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  // Handle save
  const handleSave = async () => {
    if (!description.trim()) {
      showToast(
        lang === "ar"
          ? "يرجى إدخال وصف الشركة"
          : "Please enter company description",
        "error"
      );
      return;
    }

    if (description.length > 1000) {
      showToast(
        lang === "ar"
          ? "يجب أن يكون وصف الشركة أقل من 1000 حرف"
          : "Company description should be less than 1000 characters",
        "error"
      );
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("description", description.trim());

      if (logoFile) {
        formData.append("file", logoFile);
      }

      const response = await fetch(
        `${USER_BASE_URL}/${VERSION}/profile/edit`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "app-version": "1.0.0",
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (data && data.response_code === 200) {
        showToast(
          lang === "ar" ? "تم الحفظ بنجاح" : "Saved successfully",
          "success"
        );
        // Refresh page
        window.location.reload();
      } else {
        showToast(
          data?.message || (lang === "ar" ? "فشل الحفظ" : "Failed to save"),
          "error"
        );
        setSaving(false);
      }
    } catch (err) {
      console.error("Error saving profile:", err);
      showToast(
        err.message || (lang === "ar" ? "فشل الحفظ" : "Failed to save"),
        "error"
      );
      setSaving(false);
    }
  };

  // Format phone number
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    if (phone.startsWith("971")) {
      return phone.substring(3);
    }
    return phone;
  };

  // Loading screen
  if (loading) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="profile-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Page Header with Back Button */}
      <div className="profile-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="profile-page-title">{t.generalSettings}</h1>
        <div className="header-spacer"></div>
      </div>

      {/* Main Content */}
      <div className="profile-content">
        {/* Warning Banner */}
        {!profile.general_profile_completed && (
          <div className="profile-warning-banner">
            <p>{t.profileCompletionWarning}</p>
          </div>
        )}

        {/* Company Logo */}
        <div className="profile-section">
          <label className="profile-label">{t.companyLogo}</label>
          <div className="logo-upload-container">
            <div className="logo-preview-wrapper" onClick={(e) => {
              e.stopPropagation();
              if (!saving && fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}>
              <div className="logo-preview">
                {logoPreview ? (
                  <img src={logoPreview} alt="Company Logo" />
                ) : (
                  <div className="logo-placeholder">
                    <i className="bx bx-image"></i>
                  </div>
                )}
              </div>
              <div className="logo-upload-btn">
                <i className="bx bx-camera"></i>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpg,image/jpeg,image/svg+xml,image/webp,image/avif,image/heic,image/heif"
                onChange={handleLogoChange}
                disabled={saving}
                style={{ display: "none" }}
              />
            </div>
          </div>
        </div>

        {/* Company Description */}
        <div className="profile-section">
          <label className="profile-label">{t.companyDescription}</label>
          <textarea
            className="profile-textarea"
            placeholder={t.descriptionPlaceholder}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
            maxLength={1000}
          />
          <div className="char-count">
            {description.length}/1000
          </div>
        </div>

        {/* Save Button */}
        <button
          className="profile-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="save-spinner"></span>
              {t.saving}
            </>
          ) : (
            t.save
          )}
        </button>

        {/* Read-Only Warning */}
        <div className="profile-readonly-warning">
          <p>
            {t.readOnlyWarning}
            <br />
            <a 
              href="mailto:info@fixora-app.com" 
              className="contact-us-link"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = "mailto:info@fixora-app.com";
              }}
            >
              {t.contactUs}
            </a>
          </p>
        </div>

        {/* Read-Only Fields */}
        <div className="profile-readonly-section">
          {/* Phone Number */}
          <div className="profile-field">
            <label className="profile-field-label">{t.phoneNumber}</label>
            <div className="profile-field-value disabled">
              <span className="flag">🇦🇪</span>
              <span>+971</span>
              <span>{formatPhoneNumber(profile.phone || "")}</span>
            </div>
          </div>

          {/* Company Name */}
          <div className="profile-field">
            <label className="profile-field-label">{t.companyName}</label>
            <input
              type="text"
              className="profile-input disabled"
              value={profile.name || ""}
              disabled
            />
          </div>

          {/* WhatsApp Number */}
          {profile.whatsapp_number && (
            <div className="profile-field">
              <label className="profile-field-label">{t.whatsappNumber}</label>
              <div className="profile-field-value disabled">
                <span className="flag">🇦🇪</span>
                <span>+971</span>
                <span>{formatPhoneNumber(profile.whatsapp_number)}</span>
              </div>
            </div>
          )}

          {/* Company Email */}
          <div className="profile-field">
            <label className="profile-field-label">{t.companyEmail}</label>
            <input
              type="email"
              className="profile-input disabled"
              value={profile.email || ""}
              disabled
            />
          </div>

          {/* Authorized Person Name */}
          {profile.authorized_person_name && (
            <div className="profile-field">
              <label className="profile-field-label">
                {t.authorizedPersonName}
              </label>
              <input
                type="text"
                className="profile-input disabled"
                value={profile.authorized_person_name}
                disabled
              />
            </div>
          )}

          {/* Authorized Person Designation */}
          {profile.authorized_person_designation && (
            <div className="profile-field">
              <label className="profile-field-label">
                {t.authorizedPersonDesignation}
              </label>
              <input
                type="text"
                className="profile-input disabled"
                value={profile.authorized_person_designation}
                disabled
              />
            </div>
          )}

          {/* Address Line 1 */}
          {profile.address1 && (
            <div className="profile-field">
              <label className="profile-field-label">{t.addressLine1}</label>
              <input
                type="text"
                className="profile-input disabled"
                value={profile.address1}
                disabled
              />
            </div>
          )}

          {/* Address Line 2 */}
          {profile.address2 && (
            <div className="profile-field">
              <label className="profile-field-label">{t.addressLine2}</label>
              <input
                type="text"
                className="profile-input disabled"
                value={profile.address2}
                disabled
              />
            </div>
          )}

          {/* City */}
          {profile.city && (
            <div className="profile-field">
              <label className="profile-field-label">{t.city}</label>
              <input
                type="text"
                className="profile-input disabled"
                value={profile.city}
                disabled
              />
            </div>
          )}

          {/* Emirate/State */}
          {profile.state && (
            <div className="profile-field">
              <label className="profile-field-label">{t.emirate}</label>
              <input
                type="text"
                className="profile-input disabled"
                value={profile.state}
                disabled
              />
            </div>
          )}

          {/* PO Box */}
          {profile.pincode && (
            <div className="profile-field">
              <label className="profile-field-label">{t.poBox}</label>
              <input
                type="text"
                className="profile-input disabled"
                value={profile.pincode}
                disabled
              />
            </div>
          )}

          {/* Location On Map */}
          {profile.location_coordinates && profile.location_coordinates.length === 2 && (
            <div className="profile-field">
              <label className="profile-field-label">{t.locationOnMap}</label>
              <div className="profile-map-container">
                <div
                  ref={mapRef}
                  className="profile-map-view"
                  style={{ height: "300px", width: "100%" }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="settings" lang={lang} />
    </div>
  );
}

